import * as React from 'react';
import * as styles from './Toolbox.css';
import { IAButton } from '../../../components/button/Button';
import { IAColorPicker } from '../../../components/colorPicker/ColorPicker';
import { IAFontStyle } from '../../../components/fontStyle/FontStyle';
import { IANumberField } from '../../../components/numberField/NumberField';
import { IAOptionSelector } from '../../../components/optionSelector/OptionSelector';
import { IATermCheckbox } from '../../../components/termCheckBox';
import { IATextField } from '../../../components/textField/TextField';
import { IAToggle } from '../../../components/toggle/Toggle';
import UploaderService from '../../../services/UploaderService';
import { IATreeview } from '../../treeview/Treeview';
import { ITreeViewItem } from '../../../interfaces/ITreeViewItem';
import { Label } from 'office-ui-fabric-react';
import { IASpinner } from '../../spinner/Spinner';
import { Helper } from '../../../Helper';
import { IComposerImage } from '../../../interfaces/IComposerImage';
import { LocalizationService } from '../../../services/LocalizationService';
import { inject, observer } from 'mobx-react';
import { Store } from '../stores';
import { IAFileUploader, IAFileUploaderFile } from '../../fileUploader/FileUploader';
import { Environment } from '../../../services/Environment';
import { IACheckbox } from '../../checkbox/Checkbox';
import { IComposerVideo } from '../../../interfaces/IComposerVideo';
import { Cover } from '../cover/Cover';
import { Image } from '../image/Image';
import { Video } from '../video/Video';
import { Audio } from '../audio/Audio';
import { Gallery } from '../gallery/Gallery';
import { Guide } from './guide/Guide';
import { IACard } from '../../card/Card';
import { IOrgAssetPermissionFolder } from '../../../interfaces/OrgAssets/IOrgAssetPermissionFolder';
import { IAMessageBar } from '../../messageBar/MessageBar';
import { IATooltip } from '../../tooltip/Tooltip';
import { IAIcon } from '../../icon/Icon';
import { IADropDown } from '../../dropDown/DropDown';
import { IComposerAudio } from '../../../interfaces/IComposerAudio';
import { IASlider } from '../../slider/Slider';

export interface IProps {
  store?: Store;
  onChange: () => void;
  onSelect: (tool: string) => void;
  onDragEnd?: () => void;
  onDrag?: (component: string) => void;
}

export interface IState {
  isLoadingUploadFolders: boolean;
  isLoadingReadAccessFolders: boolean;
  uploadFolders: ITreeViewItem[];
  readAccessFolders: ITreeViewItem[];
  tableLastCellId: string;
  cellSelection: Selection;
}

const textSizeMap = {
  14: "small",
  16: "medium",
  20: "large"
};

@inject('store')
@observer
export class Toolbox extends React.Component<IProps, IState> {
  private readonly BORDER_RADIUS = 5;
  private readonly localizationService: LocalizationService = new LocalizationService();

  constructor(props: IProps) {
    super(props);
    this.state = {
      isLoadingUploadFolders: false,
      isLoadingReadAccessFolders: false,
      uploadFolders: undefined,
      readAccessFolders: undefined,
      tableLastCellId: "",
      cellSelection: null,
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  private getOrgAssetsWithUploadPermissions(): void {
    this.setState({ isLoadingUploadFolders: true });
    const uploadFolders = [];
    UploaderService.getOrgAssetsWithUploadPermissions(this.props.store!.environment, this.props.store!.token, this.props.store!.tenant).then((data) => {
      if (data) {
        data.forEach((folder) => {
          uploadFolders.push(this.convertToTreeViewObject(folder));
        });
      }
      this.setState({ uploadFolders: uploadFolders, isLoadingUploadFolders: false });
    });
  }

  private getOrgAssetsWithReadPermissions(): void {
    this.setState({ isLoadingReadAccessFolders: true });
    const readAccessFolders = [];
    UploaderService.getOrgAssetsWithReadPermissions(this.props.store!.environment, this.props.store!.token, this.props.store!.tenant).then((data) => {
      if (data) {
        data.forEach((folder: any) => {
          readAccessFolders.push(this.convertToTreeViewObject(folder));
        });
      }
      this.setState({ readAccessFolders: readAccessFolders, isLoadingReadAccessFolders: false });
    });
  }

  private convertToTreeViewObject(folder: IOrgAssetPermissionFolder, title?: string): ITreeViewItem {
    const titleToUse = title ?? folder.title;
    const children = [];
    if (folder.folders && folder.folders.length > 0) {
      folder.folders.forEach((subFolder: any) => {
        children.push(this.convertToTreeViewObject(subFolder, titleToUse));
      });
    }
    return {
      title: folder.title ? folder.title : folder.name,
      children: children,
      checked: false,
      name: folder.serverRelativeUrl ? folder.serverRelativeUrl : folder.title,
      orgAssetInfo: {
        id: folder.id,
        site: folder.serverRelativeUrl.replace("/sites/", "").split("/")[0],
        title: titleToUse
      }
    }
  }

  private updateTable(): void {
    this.props.onChange();
    setTimeout(() => {
      let rows = "";
      const tableEditor: any = document.getElementById(`IA_TableEditor_${this.props.store!.activeComponent.id}`);
      if (tableEditor) {
        for (var i = 0; i < tableEditor.rows.length; i++) {
          let columns = "";
          for (var j = 0; j < tableEditor.rows[i].cells.length; j++) {
            columns += `<th style="${tableEditor.rows[i].cells[j].style.cssText}">${tableEditor.rows[i].cells[j].innerHTML}</th>`;
          }
          rows += `<tr style="${tableEditor.rows[i].style.cssText}">${columns}</tr>`;
        }
        console.log(`<table>${rows}</table>`);
        this.props.store!.activeComponent.table = `<table>${rows}</table>`;
      }
    }, 0);
    this.props.onChange();
  }

  public render(): JSX.Element {
    let toolboxContent;
    if (this.props.store!.activeComponent) {
      switch (this.props.store!.activeComponent.type) {
        case "image":
          toolboxContent = (
            <div>
              <div className={styles.IA_componentInfo}>
                {this.localizationService.strings.Composer_UploadImageOrPickOne}
              </div>
              {this.props.store!.isMobile &&
                <Image
                  image={this.props.store!.activeComponent.image ? this.props.store!.activeComponent.image : undefined}
                  key={this.props.store!.activeComponent.id}
                  id={this.props.store!.activeComponent.id}
                  hideToolbox={false}
                  showDropZones={false}
                  isInMobileToolbox
                  onChange={(image: IComposerImage) => {
                    this.props.store!.activeComponent.image = image;
                    this.forceUpdate();
                  }}
                />
              }
              <IAOptionSelector
                label={this.localizationService.strings.Composer_SelectImageSource}
                options={[
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Cloud%20Arrow%20Up/SVG/ic_fluent_cloud_arrow_up_48_regular.svg",
                    key: "Upload",
                    text: this.localizationService.strings.Composer_Upload
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Link/SVG/ic_fluent_link_48_regular.svg",
                    key: "Link",
                    text: this.localizationService.strings.Composer_FromALink
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Tab%20Desktop%20Image/SVG/ic_fluent_tab_desktop_image_24_regular.svg",
                    key: "OrganizationalAssets",
                    text: this.localizationService.strings.Composer_FromOrgAssets
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Image%20Search/SVG/ic_fluent_image_search_24_regular.svg",
                    key: "StockPhotos",
                    text: this.localizationService.strings.Composer_StockPhotos
                  }
                ]}
                selected={this.props.store!.activeComponent.image?.source}
                highlightColor={this.props.store!.highlightColor}
                compact
                disabled={this.props.store!.activeComponent.image?.url != undefined}
                iconSize={30}
                onChange={(source: any) => {
                  this.props.store!.activeComponent.image.source = source;
                  if (source === "OrganizationalAssets") {
                    this.props.store!.showOrganizationalAssets = true;
                  }
                  if (source === "StockPhotos") {
                    this.props.store!.showStockPhotos = true;
                  }
                  this.props.store!.activeComponent.image.imageOrientation = "Custom";
                  this.props.store!.activeComponent.image.width = 620;
                  this.props.onChange();
                }}
                style={{ clear: "none", marginTop: 0 }}
                borderRadius={this.BORDER_RADIUS}
              />
              {this.props.store!.activeComponent.image?.source === "Upload" &&
                <IAToggle
                  checked={this.props.store!.activeComponent.image?.organizationalAsset}
                  label={this.localizationService.strings.Composer_UploadToOrgAssets}
                  color={this.props.store!.highlightColor}
                  style={{ marginTop: 20 }}
                  disabled={this.props.store!.activeComponent.image?.url != undefined}
                  onChange={() => {
                    if (this.props.store!.activeComponent.image?.organizationalAsset) {
                      this.props.store!.activeComponent.image.organizationalAsset = false;
                      this.props.store!.activeComponent.image.organizationalAssetPath = undefined;
                    } else {
                      if (!this.state.uploadFolders) {
                        this.getOrgAssetsWithUploadPermissions();
                      }
                      this.props.store!.activeComponent.image.organizationalAsset = this.props.store!.activeComponent.image?.organizationalAsset != undefined ? !this.props.store!.activeComponent.image?.organizationalAsset : true
                    }
                    this.props.onChange();
                  }}
                />
              }
              {this.props.store!.activeComponent.image?.source === "Upload" && this.props.store!.activeComponent.image?.organizationalAsset && (this.state.uploadFolders || this.state.isLoadingUploadFolders) &&
                <div style={{
                  marginTop: 10,
                  float: "left",
                  opacity: this.props.store!.activeComponent.image?.url != undefined ? 0.5 : 1
                }}
                >
                  <Label>{this.localizationService.strings.Composer_UploadImageToFolder}</Label>
                </div>
              }
              {this.props.store!.activeComponent.image?.source === "Upload" && this.props.store!.activeComponent.image?.organizationalAsset && this.state.isLoadingUploadFolders &&
                <div style={{
                  marginTop: 10,
                  float: "left",
                  width: "100%"
                }}
                >
                  <IASpinner color={this.props.store!.highlightColor} key={Helper.getRandomStringKey()} />
                </div>
              }
              {this.props.store!.activeComponent.image?.source === "Upload" && this.props.store!.activeComponent.image?.organizationalAsset && this.state.uploadFolders &&
                <div style={{
                  marginLeft: 40,
                  width: 220,
                  float: "left"
                }}>
                  <IATreeview
                    highlightColor={this.props.store!.highlightColor}
                    actionType="Radio"
                    disabled={this.props.store!.activeComponent.image?.url != undefined}
                    structureType="Folder"
                    onSelect={(item: ITreeViewItem) => {
                      if (item) {
                        this.props.store!.activeComponent.image.organizationalAssetPath = item.name;
                      }
                    }}
                    onChange={(uploadFolders) => {
                      this.setState({ uploadFolders });
                      this.props.onChange();
                    }}
                    items={this.state.uploadFolders}
                  />
                </div>
              }
              <IATextField
                label={this.localizationService.strings.Composer_ImageURL}
                text={this.props.store!.activeComponent && this.props.store!.activeComponent.image ? this.props.store!.activeComponent.image?.url : undefined}
                style={{
                  marginTop: 20
                }}
                borderRadius={this.BORDER_RADIUS}
                highlightColor={this.props.store!.highlightColor}
                disabled={this.props.store!.activeComponent.image?.source !== "Link"}
                onChange={(url: string) => {
                  if (url.length > 0 && url !== "") {
                    this.props.store!.activeComponent.image.url = url;
                  } else {
                    this.props.store!.activeComponent.image.url = undefined;
                  }
                  this.props.onChange();
                }}
                onlyPaste={true}
              />
              <IATextField
                label={this.localizationService.strings.Composer_ALTText}
                text={this.props.store!.activeComponent && this.props.store!.activeComponent.image ? this.props.store!.activeComponent.image?.altText : undefined}
                style={{
                  marginTop: 20
                }}
                borderRadius={this.BORDER_RADIUS}
                highlightColor={this.props.store!.highlightColor}
                onChange={(altText: string) => {
                  if (altText.length > 0 && altText !== "") {
                    this.props.store!.activeComponent.image.altText = altText;
                  } else {
                    this.props.store!.activeComponent.image.altText = undefined;
                  }
                  this.props.onChange();
                }}
              />
              <IATextField
                label={this.localizationService.strings.Composer_URLLink}
                text={this.props.store!.activeComponent && this.props.store!.activeComponent.image ? this.props.store!.activeComponent.image?.urlLink : undefined}
                style={{
                  marginTop: 20
                }}
                borderRadius={this.BORDER_RADIUS}
                highlightColor={this.props.store!.highlightColor}
                onChange={(urlLink: string) => {
                  if (urlLink.length > 0 && urlLink !== "") {
                    this.props.store!.activeComponent.image.urlLink = urlLink;
                  } else {
                    this.props.store!.activeComponent.image.urlLink = undefined;
                  }
                  this.props.onChange();
                }}
              />
              <IACheckbox
                defaultColor="white"
                disabled={this.props.store!.activeComponent.image?.urlLink ? false : true}
                highlightColor={this.props.store!.highlightColor}
                isChecked={this.props.store!.linkEditorImageTarget === "_blank"}
                label={this.localizationService.strings.Composer_OpenInNewTab}
                onChange={() => {
                  this.props.store!.linkEditorImageTarget = this.props.store!.linkEditorImageTarget === "_blank" ? "_self" : "_blank";
                  this.props.store!.activeComponent.image.openInNewTab = this.props.store!.activeComponent.image?.openInNewTab === "_blank" ? "_self" : "_blank";
                }}
                styles={{
                  float: "left",
                  marginTop: 20,
                }}
              />
              {this.props.store!.activeComponent && this.props.store!.activeComponent.image && this.props.store!.activeComponent.image?.url &&
                <IAOptionSelector
                  label={this.localizationService.strings.Composer_Format}
                  options={[
                    {
                      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Crop/SVG/ic_fluent_crop_24_regular.svg",
                      key: "Custom",
                      text: this.localizationService.strings.Composer_Custom
                    },
                    {
                      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Rectangle%20Landscape/SVG/ic_fluent_rectangle_landscape_48_regular.svg",
                      key: "Horizontal",
                      text: `${this.localizationService.strings.Composer_Horizontal} (16:9)`
                    },
                    {
                      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/extra%20icons/ic_portrait_mode.svg",
                      key: "Vertical",
                      text: `${this.localizationService.strings.Composer_Vertical} (3:4)`
                    }
                  ]}
                  selected={this.props.store!.activeComponent.image && this.props.store!.activeComponent.image?.imageOrientation ? this.props.store!.activeComponent.image?.imageOrientation : "Custom"}
                  highlightColor={this.props.store!.highlightColor}
                  compact
                  iconSize={40}
                  onChange={(display: any) => {
                    if (!this.props.store!.activeComponent.image) {
                      this.props.store!.activeComponent.image = { imageOrientation: display };
                    } else {
                      this.props.store!.activeComponent.image.imageOrientation = display;
                    }
                    if (display === "Custom") {
                      this.props.store!.activeComponent.image.width = 620;
                    }
                    this.props.store!.activeComponent.image.cropSettings = null;
                    this.props.onChange();
                  }}
                  style={{ clear: "none", marginTop: 20 }}
                  borderRadius={this.BORDER_RADIUS}
                />
              }
              {this.props.store!.activeComponent && this.props.store!.activeComponent.image && this.props.store!.activeComponent.image?.imageOrientation === "Custom" &&
                <IANumberField
                  label={this.localizationService.strings.Composer_Width}
                  number={this.props.store!.activeComponent.image?.width}
                  onChange={(width: number) => {
                    this.props.store!.activeComponent.image.width = width;
                    this.props.onChange();
                  }}
                  max={620}
                  styles={{
                    width: "100%",
                  }}
                  borderRadius={this.BORDER_RADIUS}
                  highlightColor={this.props.store!.highlightColor}
                />
              }
              <IAOptionSelector
                label={this.localizationService.strings.Messages_Settings_BorderRadius}
                compact
                borderRadius={5}
                options={[
                  {
                    image: "https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/borderRadius0.png",
                    key: "0"
                  },
                  {
                    image: "https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/borderRadius5.png",
                    key: "5"
                  },
                  {
                    image: "https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/borderRadius10.png",
                    key: "10"
                  },
                  {
                    image: "https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/borderRadius20.png",
                    key: "20"
                  },
                ]}
                selected={this.props.store!.activeComponent.image?.borderRadius ? `${this.props.store!.activeComponent.image?.borderRadius}` : "0"}
                highlightColor={this.props.store!.highlightColor}
                onChange={(borderRadius: string) => {
                  this.props.store!.activeComponent.image.borderRadius = parseInt(borderRadius);
                  this.props.onChange();
                }}
                style={{ clear: "none", marginTop: 20 }}
              />
            </div>
          );
          break;
        case "gallery":
          const images = [];
          {
            this.props.store!.activeComponent.gallery.images &&
              this.props.store!.activeComponent.gallery.images.forEach((image: IComposerImage, index) => {
                const isFirstElement = index === 0;
                const isLastElement = index === this.props.store!.activeComponent.gallery.images.length - 1;
                images.push(
                  <div
                    style={{
                      paddingTop: this.props.store!.activeComponent.gallery.images?.length > 0 ? 20 : 0,
                      width: "100%"
                    }}
                  >
                    <IACard
                      key={`image${image?.url}${index}`}
                      id={image?.url}
                      environment={this.props.store!.environment}
                      tenant={this.props.store!.tenant}
                      token={this.props.store!.token}
                      cover={{
                        imageURL: image?.url
                      }}
                      isMobile={this.props.store!.isMobile}
                      alwaysShowFooter
                      footerElements={[
                        {
                          color: isLastElement ? this.props.store!.highlightColor + "80" : this.props.store!.highlightColor,
                          iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Down/SVG/ic_fluent_chevron_down_48_regular.svg",
                          onClick: () => {
                            if (!isLastElement) {
                              this.props.store!.activeComponent.gallery.images.splice(index + 1, 0, this.props.store!.activeComponent.gallery.images.splice(index, 1)[0]);
                              this.forceUpdate();
                            }
                          }
                        },
                        {
                          color: isFirstElement ? this.props.store!.highlightColor + "80" : this.props.store!.highlightColor,
                          iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Chevron%20Up/SVG/ic_fluent_chevron_up_48_regular.svg",
                          onClick: () => {
                            if (!isFirstElement) {
                              this.props.store!.activeComponent.gallery.images.splice(index - 1, 0, this.props.store!.activeComponent.gallery.images.splice(index, 1)[0]);
                              this.forceUpdate();
                            }
                          }
                        },
                        {
                          color: this.props.store!.highlightColor,
                          icon: "Trash",
                          onClick: () => {
                            this.props.store!.activeComponent.gallery.images.splice(index, 1);
                            this.forceUpdate();
                          }
                        }
                      ]}
                      width={250}
                      styles={{
                        marginBottom: 20,
                        marginTop: 10,
                        color: "#333333",
                        width: "100%"
                      }}
                      design={{
                        highlightColor: this.props.store!.highlightColor,
                        showDropShadow: true,
                        borderRadius: this.BORDER_RADIUS,
                        backgroundColor: "#f6f6f5"
                      }}
                    />
                    <IATextField
                      label={this.localizationService.strings.Composer_URL}
                      text={image?.url ? image?.url : this.localizationService.strings.Composer_Uploading}
                      style={{
                        marginTop: -12
                      }}
                      borderRadius={this.BORDER_RADIUS}
                      highlightColor={this.props.store!.highlightColor}
                      disabled={image?.source !== "Link"}
                      onChange={(url: string) => {
                        console.log(url); // Disabled
                      }}
                    />
                    <IATextField
                      label={this.localizationService.strings.Composer_ALTText}
                      text={image?.altText}
                      style={{
                        marginTop: 5
                      }}
                      borderRadius={this.BORDER_RADIUS}
                      highlightColor={this.props.store!.highlightColor}
                      onChange={(altText) => {
                        this.props.store!.activeComponent.gallery.images[index].altText = altText;
                        this.props.onChange();
                      }}
                    />
                  </div >
                );
              })
          }
          toolboxContent = (
            <div>
              <div className={styles.IA_componentInfo}>
                {this.localizationService.strings.Composer_ImageInfo}
              </div>
              {this.props.store!.isMobile &&
                <Gallery
                  gallery={this.props.store!.activeComponent.gallery ? this.props.store!.activeComponent.gallery : undefined}
                  key={this.props.store!.activeComponent.id}
                  id={this.props.store!.activeComponent.id}
                  hideToolbox={false}
                  showDropZones={false}
                  isInMobileToolbox
                  onChange={(image: IComposerImage) => {
                    this.props.store!.activeComponent.image = image;
                  }}
                />
              }
              <IAOptionSelector
                label={this.localizationService.strings.Composer_SelectSourceOfGallery}
                options={[
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Cloud%20Arrow%20Up/SVG/ic_fluent_cloud_arrow_up_48_regular.svg",
                    key: "Upload",
                    text: this.localizationService.strings.Composer_Upload
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Tab%20Desktop%20Image/SVG/ic_fluent_tab_desktop_image_24_regular.svg",
                    key: "OrganizationalAssets",
                    text: this.localizationService.strings.Composer_FromOrgAssets
                  }
                ]}
                selected={this.props.store!.activeComponent.gallery.source}
                disabled={this.props.store!.activeComponent.gallery.images.length > 0}
                highlightColor={this.props.store!.highlightColor}
                compact
                iconSize={30}
                onChange={(source: any) => {
                  this.props.store!.activeComponent.gallery.source = source;
                  this.props.store!.activeComponent.gallery.images = [];
                  if (source === "OrganizationalAssets") {
                    if (!this.state.uploadFolders) {
                      this.getOrgAssetsWithReadPermissions();
                    }
                  }
                  this.props.onChange();
                }}
                style={{ clear: "none", marginTop: 0 }}
                borderRadius={this.BORDER_RADIUS}
              />
              {this.props.store!.activeComponent.gallery.source === "OrganizationalAssets" &&
                <div style={{
                  marginTop: 10,
                  float: "left"
                }}
                >
                  <Label>{this.localizationService.strings.Composer_SelectAGalleryFolder}</Label>
                </div>
              }
              {this.props.store!.activeComponent.gallery.source === "OrganizationalAssets" && this.state.isLoadingReadAccessFolders &&
                <div style={{
                  marginTop: 10,
                  float: "left",
                  width: "100%"
                }}
                >
                  <IASpinner color={this.props.store!.highlightColor} key={Helper.getRandomStringKey()} />
                </div>
              }
              {this.props.store!.activeComponent.gallery.source === "OrganizationalAssets" && !this.state.isLoadingReadAccessFolders &&
                <>
                  <div style={{
                    marginLeft: 40,
                    width: 220,
                    float: "left"
                  }}>
                    <IATreeview
                      highlightColor={this.props.store!.highlightColor}
                      actionType="Radio"
                      structureType="Folder"
                      onSelect={(item: ITreeViewItem) => {
                        if (item) {
                          this.props.store!.activeComponent.gallery.organizationalAssetPath = item.name;
                          this.props.store!.activeComponent.gallery.organizationalAssetInfo = item.orgAssetInfo;
                        }
                        this.props.onChange();
                        this.forceUpdate();
                      }}
                      onChange={(readAccessFolders) => {
                        this.setState({ readAccessFolders });
                        this.props.onChange();
                      }}
                      items={this.state.readAccessFolders}
                    />
                  </div>
                  <IADropDown
                    label={this.localizationService.strings.Composer_GallerySortOrder}
                    onChange={(sortProperty) => {
                      this.props.store!.activeComponent.gallery.sortOrder = sortProperty;
                      this.props.onChange();
                      this.forceUpdate();
                    }}
                    options={[
                      {
                        key: "Name asc",
                        value: this.localizationService.strings.Composer_GallerySortNameAscending
                      },
                      {
                        key: "Name desc",
                        value: this.localizationService.strings.Composer_GallerySortNameDescending
                      },
                      {
                        key: "TimeLastModified asc",
                        value: this.localizationService.strings.Composer_GallerySortModifiedAscending
                      },
                      {
                        key: "TimeLastModified desc",
                        value: this.localizationService.strings.Composer_GallerySortModifiedDescending
                      }
                    ]}
                    selectedOption={this.props.store!.activeComponent.gallery?.sortOrder ?? "TimeLastModified desc"}
                    highlightColor={this.props.store!.highlightColor}
                    borderRadius={this.BORDER_RADIUS}
                    style={{
                      width: "100%",
                      marginTop: 8
                    }}
                  />
                </>
              }
              {this.props.store!.activeComponent.gallery.source === "Upload" &&
                <IAToggle
                  checked={this.props.store!.activeComponent.gallery.organizationalAsset}
                  label={this.localizationService.strings.Composer_UploadToOrgAssets}
                  color={this.props.store!.highlightColor}
                  style={{ marginTop: 20 }}
                  disabled={this.props.store!.activeComponent.gallery.images.length > 0}
                  onChange={() => {
                    if (this.props.store!.activeComponent.gallery.organizationalAsset) {
                      this.props.store!.activeComponent.gallery.organizationalAsset = false;
                      this.props.store!.activeComponent.gallery.organizationalAssetPath = undefined;
                    } else {
                      if (!this.state.uploadFolders) {
                        this.getOrgAssetsWithUploadPermissions();
                      }
                      this.props.store!.activeComponent.gallery.organizationalAsset = this.props.store!.activeComponent.gallery.organizationalAsset != undefined ? !this.props.store!.activeComponent.gallery.organizationalAsset : true;
                    }
                    this.props.onChange();
                  }}
                />
              }
              {this.props.store!.activeComponent.gallery.source === "Upload" && this.props.store!.activeComponent.gallery.organizationalAsset && (this.state.uploadFolders || this.state.isLoadingUploadFolders) &&
                <div style={{
                  marginTop: 10,
                  float: "left",
                  opacity: this.props.store!.activeComponent.gallery.images.length > 0 ? 0.5 : 1
                }}
                >
                  <Label>{this.localizationService.strings.Composer_UploadImagesToFolder}</Label>
                </div>
              }
              {this.props.store!.activeComponent.gallery.source === "Upload" && this.props.store!.activeComponent.gallery.organizationalAsset && this.state.isLoadingUploadFolders &&
                <div style={{
                  marginTop: 10,
                  float: "left",
                  width: "100%"
                }}
                >
                  <IASpinner color={this.props.store!.highlightColor} key={Helper.getRandomStringKey()} />
                </div>
              }
              {this.props.store!.activeComponent.gallery.source === "Upload" && this.props.store!.activeComponent.gallery.organizationalAsset && this.state.uploadFolders &&
                <div style={{
                  marginLeft: 40,
                  width: 220,
                  float: "left"
                }}>
                  <IATreeview
                    highlightColor={this.props.store!.highlightColor}
                    actionType="Radio"
                    disabled={this.props.store!.activeComponent.gallery.images.length > 0}
                    structureType="Folder"
                    onSelect={(item: ITreeViewItem) => {
                      if (item) {
                        this.props.store!.activeComponent.gallery.organizationalAssetPath = item.name;
                      }
                      this.props.onChange();
                    }}
                    onChange={(uploadFolders) => {
                      this.setState({ uploadFolders });
                      this.props.onChange();
                    }}
                    items={this.state.uploadFolders}
                  />
                </div>
              }
              <IAOptionSelector
                label={this.localizationService.strings.Composer_SelectGalleryType}
                options={[
                  {
                    icon: "Box view",
                    key: "Tiles",
                    text: this.localizationService.strings.Composer_Tiles
                  },
                  {
                    icon: "Collage",
                    key: "Collage",
                    text: this.localizationService.strings.Composer_Collage
                  },
                  {
                    icon: "Rotator with thumbnail",
                    key: "Rotator",
                    text: this.localizationService.strings.Composer_Rotator
                  }
                ]}
                selected={this.props.store!.activeComponent.gallery.type}
                highlightColor={this.props.store!.highlightColor}
                compact
                iconSize={30}
                onChange={(type: "Tiles" | "Collage" | "Rotator") => {
                  this.props.store!.activeComponent.gallery.type = type;
                  this.props.onChange();
                }}
                style={{ clear: "none", marginTop: 20 }}
                borderRadius={this.BORDER_RADIUS}
              />
              {this.props.store!.activeComponent.gallery.type !== "Rotator" &&
                <IANumberField
                  label={this.localizationService.strings.Composer_NumberOfVisibleImages}
                  number={this.props.store!.activeComponent.gallery.maxImages}
                  max={10}
                  onChange={(maxImages: number) => {
                    this.props.store!.activeComponent.gallery.maxImages = maxImages;
                    this.props.onChange();
                  }}
                  styles={{
                    width: "100%",
                  }}
                  borderRadius={this.BORDER_RADIUS}
                  highlightColor={this.props.store!.highlightColor}
                />
              }
              <IAOptionSelector
                label={this.localizationService.strings.Messages_Settings_BorderRadius}
                compact
                borderRadius={5}
                options={[
                  {
                    image: "https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/borderRadius0.png",
                    key: "0"
                  },
                  {
                    image: "https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/borderRadius5.png",
                    key: "5"
                  },
                  {
                    image: "https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/borderRadius10.png",
                    key: "10"
                  },
                  {
                    image: "https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/borderRadius20.png",
                    key: "20"
                  },
                ]}
                selected={this.props.store!.activeComponent.gallery?.borderRadius ? `${this.props.store!.activeComponent.gallery?.borderRadius}` : "0"}
                highlightColor={this.props.store!.highlightColor}
                onChange={(borderRadius: string) => {
                  this.props.store!.activeComponent.gallery.borderRadius = parseInt(borderRadius);
                  this.props.onChange();
                }}
                style={{ clear: "none", marginTop: 20 }}
              />
              {this.props.store!.activeComponent.gallery.source === "Upload" && this.props.store!.activeComponent.gallery.images.length > 0 &&
                <div style={{
                  float: "left",
                  marginTop: 20
                }}>
                  {`${this.localizationService.strings.Composer_GalleryImages}:`}
                </div>
              }
              {this.props.store!.activeComponent.gallery.source === "Upload" &&
                images
              }
            </div>
          );
          break;
        case "video":
          toolboxContent = (
            <div>
              <div className={styles.IA_componentInfo}>
                {this.localizationService.strings.Composer_VideoInfo}
              </div>
              {this.props.store!.isMobile &&
                <Video
                  video={this.props.store!.activeComponent.video ? this.props.store!.activeComponent.video : undefined}
                  key={this.props.store!.activeComponent.id}
                  id={this.props.store!.activeComponent.id}
                  hideToolbox={false}
                  showDropZones={false}
                  isInMobileToolbox
                  onChange={(video: IComposerVideo) => {
                    this.props.store!.activeComponent.video = video;
                    this.forceUpdate();
                  }}
                />
              }
              <IAOptionSelector
                label={this.localizationService.strings.Composer_SelectVideoSource}
                options={[
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Code/SVG/ic_fluent_code_24_regular.svg",
                    key: "Embed",
                    text: this.localizationService.strings.Composer_EmbedCode
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Play%20Circle/SVG/ic_fluent_play_circle_48_regular.svg",
                    key: "Microsoft",
                    text: this.localizationService.strings.Composer_MicrosoftVideo
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Link/SVG/ic_fluent_link_48_regular.svg",
                    key: "Link",
                    text: this.localizationService.strings.Composer_FromALink
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Cloud%20Arrow%20Up/SVG/ic_fluent_cloud_arrow_up_48_regular.svg",
                    key: "Upload",
                    text: this.localizationService.strings.Composer_Upload
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Tab%20Desktop%20Image/SVG/ic_fluent_tab_desktop_image_24_regular.svg",
                    key: "OrganizationalAssets",
                    text: this.localizationService.strings.Composer_FromOrgAssets
                  }
                ]}
                selected={this.props.store!.activeComponent.video?.source}
                highlightColor={this.props.store!.highlightColor}
                compact
                disabled={this.props.store!.activeComponent.video?.url != undefined || this.props.store!.activeComponent.video?.embedCode != undefined}
                iconSize={30}
                onChange={(source: any) => {
                  this.props.store!.activeComponent.video.source = source;
                  if (source === "OrganizationalAssets") {
                    this.props.store!.showOrganizationalAssets = true;
                  }
                  this.props.onChange();
                }}
                style={{ clear: "none", marginTop: 0 }}
                borderRadius={this.BORDER_RADIUS}
              />
              {this.props.store!.activeComponent.video?.source === "Upload" &&
                <IAToggle
                  checked={this.props.store!.activeComponent.video?.organizationalAsset}
                  label={this.localizationService.strings.Composer_UploadToOrgAssets}
                  color={this.props.store!.highlightColor}
                  style={{ marginTop: 20 }}
                  disabled={this.props.store!.activeComponent.video?.url != undefined}
                  onChange={() => {
                    if (this.props.store!.activeComponent.video?.organizationalAsset) {
                      this.props.store!.activeComponent.video.organizationalAsset = false;
                      this.props.store!.activeComponent.video.organizationalAssetPath = undefined;
                    } else {
                      if (!this.state.uploadFolders) {
                        this.getOrgAssetsWithUploadPermissions();
                      }
                      this.props.store!.activeComponent.video.organizationalAsset = this.props.store!.activeComponent.video?.organizationalAsset != undefined ? !this.props.store!.activeComponent.video?.organizationalAsset : true;
                    }
                    this.props.onChange();
                  }}
                />
              }
              {this.props.store!.activeComponent.video?.source === "Upload" && this.props.store!.activeComponent.video?.organizationalAsset && (this.state.uploadFolders || this.state.isLoadingUploadFolders) &&
                <div style={{
                  marginTop: 10,
                  float: "left",
                  opacity: this.props.store!.activeComponent.video?.url != undefined ? 0.5 : 1
                }}
                >
                  <Label>{this.localizationService.strings.Composer_UploadVideoToFolder}</Label>
                </div>
              }
              {this.props.store!.activeComponent.video?.source === "Upload" && this.props.store!.activeComponent.video?.organizationalAsset && this.state.isLoadingUploadFolders &&
                <div style={{
                  marginTop: 10,
                  float: "left",
                  width: "100%"
                }}
                >
                  <IASpinner color={this.props.store!.highlightColor} key={Helper.getRandomStringKey()} />
                </div>
              }
              {this.props.store!.activeComponent.video?.source === "Upload" && this.props.store!.activeComponent.video?.organizationalAsset && this.state.uploadFolders &&
                <div style={{
                  marginLeft: 40,
                  width: 220,
                  float: "left"
                }}>
                  <IATreeview
                    highlightColor={this.props.store!.highlightColor}
                    actionType="Radio"
                    disabled={this.props.store!.activeComponent.video?.url != undefined}
                    structureType="Folder"
                    onSelect={(item: ITreeViewItem) => {
                      if (item) {
                        this.props.store!.activeComponent.video.organizationalAssetPath = item.name;
                      }
                      this.props.onChange();
                    }}
                    onChange={(uploadFolders) => {
                      this.setState({ uploadFolders });
                      this.props.onChange();
                    }}
                    items={this.state.uploadFolders}
                  />
                </div>
              }
              {this.props.store!.activeComponent.video?.source === "Microsoft" &&
                <IAMessageBar
                  type="information"
                  onDismiss={() => {

                  }}
                  icon="Info"
                  content={this.localizationService.strings.Composer_MicrosoftVideoInfo}
                  showIcon={true}
                  showCloseButton={false}
                  style={{
                    marginTop: 20
                  }}
                />
              }
              {this.props.store!.activeComponent.video?.source !== "Embed" &&
                <IATextField
                  label={this.localizationService.strings.Composer_URL}
                  text={this.props.store!.activeComponent && this.props.store!.activeComponent.video ? this.props.store!.activeComponent.video?.url : undefined}
                  style={{
                    marginTop: 10
                  }}
                  toolTipText={this.props.store!.activeComponent.video?.source === "Microsoft" ? this.localizationService.strings.Composer_MicrosoftVideoPageURLTooltip : undefined}
                  borderRadius={this.BORDER_RADIUS}
                  highlightColor={this.props.store!.highlightColor}
                  disabled={this.props.store!.activeComponent.video?.source !== "Link" && this.props.store!.activeComponent.video?.source !== "Microsoft"}
                  required
                  onChange={(url: string) => {
                    let activeComponent = this.props.store!.activeComponent;
                    if (url.length > 0 && url !== "") {
                      activeComponent.video.url = Helper.convertUrlToEmbedUrl(url);
                    } else {
                      activeComponent.video.url = undefined;
                    }
                    this.props.onChange();
                  }}
                />
              }
              {(this.props.store!.activeComponent.video?.source === "Embed" || this.props.store!.activeComponent.video?.source === "Microsoft") &&
                <div>
                  <div style={{
                    fontSize: "14px",
                    marginTop: 20,
                    float: "left"
                  }}>
                    {this.localizationService.strings.Composer_EmbedCode}
                  </div>
                  {this.props.store!.activeComponent.video?.source === "Microsoft" &&
                    <IATooltip
                      content="Tooltip"
                      type="bubble"
                      rootStyles={{
                        marginTop: 2,
                        marginLeft: 5
                      }}
                      componentContent={
                        <div>
                          {this.localizationService.strings.Composer_MicrosoftVideoEmbedCodeTooltip}
                        </div>
                      }
                    >
                      <div style={{
                        fontSize: "14px",
                        marginTop: 20,
                        float: "left"
                      }}>
                        <IAIcon
                          title="Info"
                          size={14}
                          color="Black"
                        />
                      </div>
                    </IATooltip>
                  }
                  <textarea
                    value={this.props.store!.activeComponent && this.props.store!.activeComponent.video && this.props.store!.activeComponent.video?.embedCode}
                    placeholder={"<iframe... />"}
                    style={{
                      width: "calc(100% - 7px)",
                      border: "none",
                      minHeight: 100,
                      marginTop: 10,
                      fontSize: "13px",
                      padding: 7,
                      boxSizing: "border-box",
                      minWidth: "100%",
                      maxWidth: "100%",
                      borderRadius: this.BORDER_RADIUS
                    }}
                    onChange={(event) => {
                      if (event.target.value.length > 0 && event.target.value !== "") {
                        this.props.store!.activeComponent.video.embedCode = event.target.value;
                      } else {
                        this.props.store!.activeComponent.video.embedCode = undefined;
                      }
                      this.props.onChange();
                    }}
                  />
                </div>
              }
              <IATextField
                label={this.localizationService.strings.Composer_ALTText}
                text={this.props.store!.activeComponent && this.props.store!.activeComponent.video ? this.props.store!.activeComponent.video?.altText : undefined}
                style={{
                  marginTop: 20
                }}
                borderRadius={this.BORDER_RADIUS}
                highlightColor={this.props.store!.highlightColor}
                onChange={(altText: string) => {
                  let activeComponent = this.props.store!.activeComponent;
                  if (altText.length > 0 && altText !== "") {
                    activeComponent.video.altText = altText;
                  } else {
                    activeComponent.video.altText = undefined;
                  }
                  this.props.onChange();
                }}
              />
            </div>
          );
          break;
        case "audio":
          toolboxContent = (
            <div>
              <div className={styles.IA_componentInfo}>
                {this.localizationService.strings.Composer_AudioInfo}
              </div>
              {this.props.store!.isMobile &&
                <Audio
                  audio={this.props.store!.activeComponent.audio ? this.props.store!.activeComponent.audio : undefined}
                  key={this.props.store!.activeComponent.id}
                  id={this.props.store!.activeComponent.id}
                  hideToolbox={false}
                  showDropZones={false}
                  isInMobileToolbox
                  onChange={(audio: IComposerAudio) => {
                    this.props.store!.activeComponent.audio = audio;
                    this.forceUpdate();
                  }}
                />
              }
              <IAOptionSelector
                label={this.localizationService.strings.Composer_SelectAudioSource}
                options={[
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Link/SVG/ic_fluent_link_48_regular.svg",
                    key: "Link",
                    text: this.localizationService.strings.Composer_FromALink
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Cloud%20Arrow%20Up/SVG/ic_fluent_cloud_arrow_up_48_regular.svg",
                    key: "Upload",
                    text: this.localizationService.strings.Composer_Upload
                  },
                  // {
                  //   iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Tab%20Desktop%20Image/SVG/ic_fluent_tab_desktop_image_24_regular.svg",
                  //   key: "OrganizationalAssets",
                  //   text: this.localizationService.strings.Composer_FromOrgAssets
                  // }
                ]}
                selected={this.props.store!.activeComponent.audio.source}
                highlightColor={this.props.store!.highlightColor}
                compact
                disabled={this.props.store!.activeComponent.audio.url != undefined}
                iconSize={30}
                onChange={(source: any) => {
                  this.props.store!.activeComponent.audio.source = source;
                  if (source === "OrganizationalAssets") {
                    this.props.store!.showOrganizationalAssets = true;
                  }
                  this.props.onChange();
                }}
                style={{ clear: "none", marginTop: 0 }}
                borderRadius={this.BORDER_RADIUS}
              />
              {/* {this.props.store!.activeComponent.audio.source === "Upload" &&
                <IAToggle
                  checked={this.props.store!.activeComponent.audio.organizationalAsset}
                  label={this.localizationService.strings.Composer_UploadToOrgAssets}
                  color={this.props.store!.highlightColor}
                  style={{ marginTop: 20 }}
                  disabled={this.props.store!.activeComponent.audio.url != undefined}
                  onChange={() => {
                    if (this.props.store!.activeComponent.audio.organizationalAsset) {
                      this.props.store!.activeComponent.audio.organizationalAsset = false;
                      this.props.store!.activeComponent.audio.organizationalAssetPath = undefined;
                    } else {
                      if (!this.state.uploadFolders) {
                        this.getOrgAssetsWithUploadPermissions();
                      }
                      this.props.store!.activeComponent.audio.organizationalAsset = this.props.store!.activeComponent.audio.organizationalAsset != undefined ? !this.props.store!.activeComponent.audio.organizationalAsset : true;
                    }
                    this.props.onChange();
                  }}
                />
              } */}
              {(this.props.store!.activeComponent.audio.source === "Link" || this.props.store!.activeComponent.audio.source === "Upload") &&
                <IATextField
                  label={this.localizationService.strings.Composer_URL}
                  text={this.props.store!.activeComponent && this.props.store!.activeComponent.audio ? this.props.store!.activeComponent.audio.url : undefined}
                  style={{
                    marginTop: 10
                  }}
                  disabled={this.props.store!.activeComponent.audio.source !== "Link"}
                  borderRadius={this.BORDER_RADIUS}
                  highlightColor={this.props.store!.highlightColor}
                  required
                  onChange={(url: string) => {
                    let activeComponent = this.props.store!.activeComponent;
                    activeComponent.audio.url = `${url?.toLowerCase().split(".mp3")[0]}.mp3`;
                    this.props.onChange();
                  }}
                />
              }
              {/* {this.props.store!.activeComponent.audio.source === "Upload" && this.props.store!.activeComponent.audio.organizationalAsset && (this.state.uploadFolders || this.state.isLoadingUploadFolders) &&
                <div style={{
                  marginTop: 10,
                  float: "left",
                  opacity: this.props.store!.activeComponent.audio.url != undefined ? 0.5 : 1
                }}
                >
                  <Label>{this.localizationService.strings.Composer_UploadAudioToFolder}</Label>
                </div>
              } */}
              {this.props.store!.activeComponent.audio.source === "Upload" && this.props.store!.activeComponent.audio.organizationalAsset && this.state.isLoadingUploadFolders &&
                <div style={{
                  marginTop: 10,
                  float: "left",
                  width: "100%"
                }}
                >
                  <IASpinner color={this.props.store!.highlightColor} key={Helper.getRandomStringKey()} />
                </div>
              }
              {/* {this.props.store!.activeComponent.audio.source === "Upload" && this.props.store!.activeComponent.audio.organizationalAsset && this.state.uploadFolders &&
                <div style={{
                  marginLeft: 40,
                  width: 220,
                  float: "left"
                }}>
                  <IATreeview
                    highlightColor={this.props.store!.highlightColor}
                    actionType="Radio"
                    disabled={this.props.store!.activeComponent.audio.url != undefined}
                    structureType="Folder"
                    onSelect={(item: ITreeViewItem) => {
                      if (item) {
                        this.props.store!.activeComponent.audio.organizationalAssetPath = item.name;
                      }
                      this.props.onChange();
                    }}
                    onChange={(uploadFolders) => {
                      this.setState({ uploadFolders });
                      this.props.onChange();
                    }}
                    items={this.state.uploadFolders}
                  />
                </div>
              } */}
              <IATextField
                label={this.localizationService.strings.Composer_ALTText}
                text={this.props.store!.activeComponent && this.props.store!.activeComponent.audio ? this.props.store!.activeComponent.audio.altText : undefined}
                style={{
                  marginTop: 20
                }}
                borderRadius={this.BORDER_RADIUS}
                highlightColor={this.props.store!.highlightColor}
                onChange={(altText: string) => {
                  let activeComponent = this.props.store!.activeComponent;
                  if (altText.length > 0 && altText !== "") {
                    activeComponent.audio.altText = altText;
                  } else {
                    activeComponent.audio.altText = undefined;
                  }
                  this.props.onChange();
                }}
              />
            </div>
          );
          break;

        case "button":

          toolboxContent = (
            <div>
              <div className={styles.IA_componentInfo}>
                {this.localizationService.strings.Composer_ButtonDescription}
              </div>

              <IATextField
                label={this.localizationService.strings.Composer_ButtonText}
                text={this.props.store!.activeComponent && this.props.store!.activeComponent.button ? this.props.store!.activeComponent.button?.text : undefined}
                style={{
                  marginTop: 20
                }}
                borderRadius={this.BORDER_RADIUS}
                highlightColor={this.props.store!.highlightColor}

                onChange={(text: string) => {
                  if (text.length > 0) {
                    this.props.store!.activeComponent.button.text = text;
                  } else {
                    this.props.store!.activeComponent.button.text = undefined;
                  }
                  this.props.onChange();
                }}

              />

              <IATextField
                label={this.localizationService.strings.Composer_URL}
                text={this.props.store!.activeComponent && this.props.store!.activeComponent.button ? this.props.store!.activeComponent.button?.url : undefined}
                style={{
                  marginTop: 20
                }}
                borderRadius={this.BORDER_RADIUS}
                highlightColor={this.props.store!.highlightColor}

                onChange={(url: string) => {
                  if (url.length > 0 && url !== "") {
                    this.props.store!.activeComponent.button.url = url;
                  } else {
                    this.props.store!.activeComponent.button.url = undefined;
                  }
                  this.props.onChange();
                }}

              />

              <IADropDown
                label={this.localizationService.strings.Composer_ButtonTextSize}
                selectedOption={textSizeMap[this.props.store!.activeComponent?.button?.textSize]}
                style={{ maxWidth: "100%", marginTop: 20, float: "left" }}
                options={[
                  { key: "small", value: this.localizationService.strings.Composer_ButtonTextSizeSmall },
                  { key: "medium", value: this.localizationService.strings.Composer_ButtonTextSizeMedium },
                  { key: "large", value: this.localizationService.strings.Composer_ButtonTextSizeLarge }
                ]}
                onChange={(key: "small" | "medium" | "large") => {

                  const sizeValues = { small: 14, medium: 16, large: 20 };

                  if (this.props.store!.activeComponent?.button) {
                    this.props.store!.activeComponent.button.textSize = sizeValues[key] as 14 | 16 | 20;
                    this.props.onChange();
                  }
                }}
              />
              <IAOptionSelector
                label={this.localizationService.strings.Composer_ButtonAlignment}
                options={[
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Text%20Align%20Left/SVG/ic_fluent_text_align_left_24_regular.svg",
                    key: "left",
                    width: 82,
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Text%20Align%20Center/SVG/ic_fluent_text_align_center_24_regular.svg",
                    key: "center",
                    width: 82,
                  },
                  {
                    iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Text%20Align%20Left%20Temp%20RTL/SVG/ic_fluent_text_align_right_24_regular.svg",
                    key: "right",
                    width: 82,
                  }
                ]}
                selected={this.props.store!.activeComponent.button.align}
                highlightColor={this.props.store!.highlightColor}
                compact
                disabled={this.props.store!.activeComponent.image?.url != undefined}

                onChange={(align: "left" | "center" | "right") => {
                  this.props.store!.activeComponent.button.align = align;
                  this.props.onChange();
                }}
                style={{ clear: "none", marginTop: 20 }}
                borderRadius={this.BORDER_RADIUS}
              />

              <IAOptionSelector
                label={this.localizationService.strings.Messages_Settings_BorderRadius}
                compact
                borderRadius={5}
                options={[
                  {
                    image: "https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/borderRadius0.png",
                    key: "0"
                  },
                  {
                    image: "https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/borderRadius5.png",
                    key: "5"
                  },
                  {
                    image: "https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/borderRadius10.png",
                    key: "10"
                  },
                  {
                    image: "https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/borderRadius20.png",
                    key: "20"
                  },
                ]}
                selected={this.props.store!.activeComponent.button?.borderRadius ? `${this.props.store!.activeComponent.button?.borderRadius}` : "0"}
                highlightColor={this.props.store!.highlightColor}
                onChange={(borderRadius: string) => {
                  this.props.store!.activeComponent.button.borderRadius = parseInt(borderRadius);
                  this.props.onChange();
                }}
                style={{ clear: "none", marginTop: 20 }}
              />

              <IAColorPicker
                label={this.localizationService.strings.Composer_ButtonTextColor}
                color={this.props.store!.activeComponent && this.props.store!.activeComponent.button ? this.props.store!.activeComponent.button?.textColor : "#ffffff"}
                defaultColor="#2800b8"
                showClearButton={true}
                onChange={(textColor: string) => {
                  this.props.store!.activeComponent.button.textColor = textColor;
                  this.props.onChange();

                }}
                styles={{
                  clear: "both",
                  width: "100%"
                }}
              />

              <IAColorPicker
                label={this.localizationService.strings.Composer_ButtonBackgroundColor}
                color={this.props.store!.activeComponent && this.props.store!.activeComponent.button ? this.props.store!.activeComponent.button?.backgroundColor : "#2b2002"}
                defaultColor="#2800b8"
                showClearButton={true}
                onChange={(backgroundColor: string) => {
                  this.props.store!.activeComponent.button.backgroundColor = backgroundColor;
                  this.updateTable();

                }}
                styles={{
                  clear: "both",
                  width: "100%"
                }}
              />
            </div>
          );
          break;


        case "divider":

          toolboxContent = (

            <div>
              <IASlider
                label={this.localizationService.strings.Composer_DividerWeight}
                min={1}
                max={12}
                value={`${this.props.store!.activeComponent.divider.weight}`}
                onChange={(value) => {

                  this.props.store!.activeComponent.divider.weight = value;
                  this.forceUpdate();
                }}
                color={this.props.store!.highlightColor}
                styles={{ width: "100%" }}
              />

              <IASlider
                label={this.localizationService.strings.Composer_DividerLength}
                min={1}
                max={100}
                value={`${this.props.store!.activeComponent.divider.length}`}
                onChange={(value) => {

                  this.props.store!.activeComponent.divider.length = value;
                  this.forceUpdate();
                }}
                color={this.props.store!.highlightColor}
                styles={{ width: "100%" }}
              />

              <IAColorPicker
                label={this.localizationService.strings.Composer_DividerColor}
                color={this.props.store!.activeComponent && this.props.store!.activeComponent.divider ? this.props.store!.activeComponent.divider?.color : "#000"}
                defaultColor="#000"
                showClearButton={true}
                onChange={(color: string) => {
                  this.props.store!.activeComponent.divider.color = color;
                  this.updateTable();
                }}
                styles={{
                  clear: "both",
                  width: "100%"
                }}
              />
            </div>
          );
          break;

        case "cover":
          toolboxContent = (
            <div>
              {this.props.store!.component !== "Event" &&
                <div className={styles.IA_componentInfo}>
                  {this.props.store!.instance?.settings?.type === "reels" ? this.localizationService.strings.Composer_CoverInfoReels : this.localizationService.strings.Composer_CoverInfo}
                </div>
              }
              {this.props.store!.isMobile && this.props.store!.component !== "Event" &&
                <Cover
                  isInMobileToolbox
                  id={this.props.store!.cover.id}
                  image={this.props.store!.cover.image}
                  video={this.props.store!.cover.video}
                  typeToEdit={this.props.store!.activeComponent && this.props.store!.activeComponent.typeToEdit}
                  hideToolbox={this.props.store!.activeComponent && this.props.store!.activeComponent.id !== this.props.store!.cover.id}
                  showDropZones={this.props.store!.componentToAdd != undefined}
                  isMobile={this.props.store!.isMobile}
                  onChangeImage={(image: IComposerImage) => {
                    this.props.store!.activeComponent.image = image;
                    this.forceUpdate();
                  }}
                  onChangeVideo={(video: IComposerVideo) => {
                    this.props.store!.activeComponent.video = video;
                    this.forceUpdate();
                  }}
                  onFocus={() => {
                    this.props.store!.activeComponent = this.props.store!.cover;
                  }}
                />
              }
              {this.props.store?.component !== "Event" && this.props.store?.instance?.settings?.type !== "reels" &&
                <IAOptionSelector
                  label={this.localizationService.strings.Composer_SelectTypeToEdit}
                  options={[
                    {
                      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Image/SVG/ic_fluent_image_48_regular.svg",
                      key: "image",
                      text: this.localizationService.strings.Composer_Image
                    },
                    {
                      iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Video%20Clip/SVG/ic_fluent_video_clip_24_regular.svg",
                      key: "video",
                      text: this.localizationService.strings.Composer_Video
                    },
                  ]}
                  selected={this.props.store!.activeComponent.typeToEdit}
                  highlightColor={this.props.store!.highlightColor}
                  compact
                  iconSize={30}
                  onChange={(type: any) => {
                    this.props.store!.activeComponent.typeToEdit = type;
                    this.props.onChange();
                  }}
                  style={{ clear: "none", marginTop: 0 }}
                  borderRadius={this.BORDER_RADIUS}
                />
              }
              {this.props.store!.activeComponent.typeToEdit === "image" &&
                <div>
                  <IAOptionSelector
                    label={this.localizationService.strings.Composer_SelectImageSource}
                    options={[
                      {
                        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Cloud%20Arrow%20Up/SVG/ic_fluent_cloud_arrow_up_48_regular.svg",
                        key: "Upload",
                        text: this.localizationService.strings.Composer_Upload
                      },
                      {
                        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Link/SVG/ic_fluent_link_48_regular.svg",
                        key: "Link",
                        text: this.localizationService.strings.Composer_FromALink
                      },
                      {
                        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Tab%20Desktop%20Image/SVG/ic_fluent_tab_desktop_image_24_regular.svg",
                        key: "OrganizationalAssets",
                        text: this.localizationService.strings.Composer_FromOrgAssets
                      },
                      {
                        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Image%20Search/SVG/ic_fluent_image_search_24_regular.svg",
                        key: "StockPhotos",
                        text: this.localizationService.strings.Composer_StockPhotos
                      }
                    ]}
                    selected={this.props.store!.activeComponent.image?.source}
                    highlightColor={this.props.store!.highlightColor}
                    compact
                    disabled={this.props.store!.activeComponent.image?.url != undefined}
                    iconSize={30}
                    onChange={(source: any) => {
                      this.props.store!.activeComponent.image.source = source;
                      if (source === "OrganizationalAssets") {
                        this.props.store!.showOrganizationalAssets = true;
                      }
                      if (source === "StockPhotos") {
                        this.props.store!.showStockPhotos = true;
                      }
                      this.props.onChange();
                    }}
                    style={{ clear: "none", marginTop: 20 }}
                    borderRadius={this.BORDER_RADIUS}
                  />
                  {this.props.store!.activeComponent.image?.source === "Upload" &&
                    <IAToggle
                      checked={this.props.store!.activeComponent.image?.organizationalAsset}
                      label={this.localizationService.strings.Composer_UploadToOrgAssets}
                      color={this.props.store!.highlightColor}
                      style={{ marginTop: 20 }}
                      disabled={this.props.store!.activeComponent.image?.url != undefined}
                      onChange={() => {
                        if (this.props.store!.activeComponent.image.organizationalAsset) {
                          this.props.store!.activeComponent.image.organizationalAsset = false;
                          this.props.store!.activeComponent.image.organizationalAssetPath = undefined;
                        } else {
                          if (!this.state.uploadFolders) {
                            this.getOrgAssetsWithUploadPermissions();
                          }
                          this.props.store!.activeComponent.image.organizationalAsset = this.props.store!.activeComponent.image?.organizationalAsset != undefined ? !this.props.store!.activeComponent.image?.organizationalAsset : true;
                        }
                        this.props.onChange();
                      }}
                    />
                  }
                  {this.props.store!.activeComponent.image?.source === "Upload" && this.props.store!.activeComponent.image?.organizationalAsset && (this.state.uploadFolders || this.state.isLoadingUploadFolders) &&
                    <div style={{
                      marginTop: 10,
                      float: "left",
                      opacity: this.props.store!.activeComponent.image?.url != undefined ? 0.5 : 1
                    }}
                    >
                      <Label>{this.localizationService.strings.Composer_UploadImageToFolder}</Label>
                    </div>
                  }
                  {this.props.store!.activeComponent.image?.source === "Upload" && this.props.store!.activeComponent.image?.organizationalAsset && this.state.isLoadingUploadFolders &&
                    <div style={{
                      marginTop: 10,
                      float: "left",
                      width: "100%"
                    }}
                    >
                      <IASpinner color={this.props.store!.highlightColor} key={Helper.getRandomStringKey()} />
                    </div>
                  }
                  {this.props.store!.activeComponent.image?.source === "Upload" && this.props.store!.activeComponent.image?.organizationalAsset && this.state.uploadFolders &&
                    <div style={{
                      marginLeft: 40,
                      width: 220,
                      float: "left"
                    }}>
                      <IATreeview
                        highlightColor={this.props.store!.highlightColor}
                        actionType="Radio"
                        disabled={this.props.store!.activeComponent.image?.url != undefined}
                        structureType="Folder"
                        onSelect={(item: ITreeViewItem) => {
                          if (item) {
                            this.props.store!.activeComponent.image.organizationalAssetPath = item.name;
                          }
                        }}
                        onChange={(uploadFolders) => {
                          this.setState({ uploadFolders });
                          this.props.onChange();
                        }}
                        items={this.state.uploadFolders}
                      />
                    </div>
                  }
                  <IATextField
                    label={this.localizationService.strings.Composer_URL}
                    text={this.props.store!.activeComponent && this.props.store!.activeComponent.image ? this.props.store!.activeComponent.image?.url : undefined}
                    style={{
                      marginTop: 20
                    }}
                    borderRadius={this.BORDER_RADIUS}
                    highlightColor={this.props.store!.highlightColor}
                    disabled={this.props.store!.activeComponent.image?.source !== "Link"}
                    onChange={(url: string) => {
                      if (url.length > 0 && url !== "") {
                        this.props.store!.activeComponent.image.url = url;
                      } else {
                        this.props.store!.activeComponent.image.url = undefined;
                      }
                      this.props.onChange();
                    }}
                    onlyPaste={true}
                  />
                  <IATextField
                    label={this.localizationService.strings.Composer_ALTText}
                    text={this.props.store!.activeComponent && this.props.store!.activeComponent.image ? this.props.store!.activeComponent.image?.altText : undefined}
                    style={{
                      marginTop: 20
                    }}
                    borderRadius={this.BORDER_RADIUS}
                    highlightColor={this.props.store!.highlightColor}
                    onChange={(altText: string) => {
                      if (altText.length > 0 && altText !== "") {
                        this.props.store!.activeComponent.image.altText = altText;
                      } else {
                        this.props.store!.activeComponent.image.altText = undefined;
                      }
                      this.props.onChange();
                    }}
                  />
                  <IAToggle
                    checked={this.props.store!.activeComponent.image?.onlyShowCoverImageInRollup}
                    label={this.localizationService.strings.Composer_OnlyShowCoverImageInRollup}
                    color={this.props.store!.highlightColor}
                    style={{ marginTop: 20 }}
                    onChange={() => {
                      this.props.store!.activeComponent.image.onlyShowCoverImageInRollup = this.props.store!.activeComponent.image?.onlyShowCoverImageInRollup ? !this.props.store!.activeComponent.image?.onlyShowCoverImageInRollup : true;
                      this.props.onChange();
                    }}
                  />
                </div>
              }
              {this.props.store!.activeComponent.typeToEdit === "video" &&
                <div>
                  <IAOptionSelector
                    label={this.localizationService.strings.Composer_SelectVideoSource}
                    options={[
                      {
                        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Cloud%20Arrow%20Up/SVG/ic_fluent_cloud_arrow_up_48_regular.svg",
                        key: "Upload",
                        text: this.localizationService.strings.Composer_Upload
                      },
                      {
                        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Play%20Circle/SVG/ic_fluent_play_circle_48_regular.svg",
                        key: "Microsoft",
                        text: this.localizationService.strings.Composer_MicrosoftVideo
                      },
                      {
                        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Code/SVG/ic_fluent_code_24_regular.svg",
                        key: "Embed",
                        text: this.localizationService.strings.Composer_EmbedCode
                      },
                      {
                        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Link/SVG/ic_fluent_link_48_regular.svg",
                        key: "Link",
                        text: this.localizationService.strings.Composer_FromALink
                      },
                      {
                        iconUrl: "https://intraactivestorage.blob.core.windows.net/cdn/icons/Image%20Search/SVG/ic_fluent_image_search_24_regular.svg",
                        key: "OrganizationalAssets",
                        text: this.localizationService.strings.Composer_FromOrgAssets
                      }
                    ]}
                    selected={this.props.store!.activeComponent.video?.source}
                    highlightColor={this.props.store!.highlightColor}
                    compact
                    disabled={this.props.store!.activeComponent.video?.url != undefined || this.props.store!.activeComponent.video?.embedCode != undefined}
                    iconSize={30}
                    onChange={(source: any) => {
                      if (source === "OrganizationalAssets") {
                        this.props.store!.showOrganizationalAssets = true;
                      }
                      this.props.store!.activeComponent.video.source = source;
                      this.props.onChange();
                    }}
                    style={{ clear: "none", marginTop: 20 }}
                    borderRadius={this.BORDER_RADIUS}
                  />
                  {this.props.store!.activeComponent.video?.source === "Upload" &&
                    <IAToggle
                      checked={this.props.store!.activeComponent.video?.organizationalAsset}
                      label={this.localizationService.strings.Composer_UploadToOrgAssets}
                      color={this.props.store!.highlightColor}
                      style={{ marginTop: 20 }}
                      disabled={this.props.store!.activeComponent.video?.url != undefined}
                      onChange={() => {
                        if (this.props.store!.activeComponent.video?.organizationalAsset) {
                          this.props.store!.activeComponent.video.organizationalAsset = false;
                          this.props.store!.activeComponent.video.organizationalAssetPath = undefined;
                        } else {
                          if (!this.state.uploadFolders) {
                            this.getOrgAssetsWithUploadPermissions();
                          }
                          this.props.store!.activeComponent.video.organizationalAsset = this.props.store!.activeComponent.video?.organizationalAsset != undefined ? !this.props.store!.activeComponent.video?.organizationalAsset : true;
                        }
                        this.props.onChange();
                      }}
                    />
                  }
                  {this.props.store!.activeComponent.video?.source === "Upload" && this.props.store!.activeComponent.video?.organizationalAsset && (this.state.uploadFolders || this.state.isLoadingUploadFolders) &&
                    <div style={{
                      marginTop: 10,
                      float: "left",
                      opacity: this.props.store!.activeComponent.video?.url != undefined ? 0.5 : 1
                    }}
                    >
                      <Label>{this.localizationService.strings.Composer_UploadVideoToFolder}</Label>
                    </div>
                  }
                  {this.props.store!.activeComponent.video?.source === "Upload" && this.props.store!.activeComponent.video?.organizationalAsset && this.state.isLoadingUploadFolders &&
                    <div style={{
                      marginTop: 10,
                      float: "left",
                      width: "100%"
                    }}
                    >
                      <IASpinner color={this.props.store!.highlightColor} key={Helper.getRandomStringKey()} />
                    </div>
                  }
                  {this.props.store!.activeComponent.video?.source === "Upload" && this.props.store!.activeComponent.video?.organizationalAsset && this.state.uploadFolders &&
                    <div style={{
                      marginLeft: 40,
                      width: 220,
                      float: "left"
                    }}>
                      <IATreeview
                        highlightColor={this.props.store!.highlightColor}
                        actionType="Radio"
                        disabled={this.props.store!.activeComponent.video?.url != undefined}
                        structureType="Folder"
                        onSelect={(item: ITreeViewItem) => {
                          if (item) {
                            this.props.store!.activeComponent.video.organizationalAssetPath = item.name;
                          }
                          this.props.onChange();
                        }}
                        onChange={(uploadFolders) => {
                          this.setState({ uploadFolders });
                          this.props.onChange();
                        }}
                        items={this.state.uploadFolders}
                      />
                    </div>
                  }
                  {this.props.store!.activeComponent.video?.source === "Microsoft" &&
                    <IAMessageBar
                      type="information"
                      onDismiss={() => {

                      }}
                      icon="Info"
                      content={this.localizationService.strings.Composer_MicrosoftVideoInfo}
                      showIcon={true}
                      showCloseButton={false}
                      style={{
                        marginTop: 20
                      }}
                    />
                  }
                  {this.props.store!.activeComponent.video?.source !== "Embed" &&
                    <IATextField
                      label={this.localizationService.strings.Composer_URL}
                      text={this.props.store!.activeComponent && this.props.store!.activeComponent.video ? this.props.store!.activeComponent.video?.url : undefined}
                      style={{
                        marginTop: 20
                      }}
                      toolTipText={this.props.store!.activeComponent.video?.source === "Microsoft" ? this.localizationService.strings.Composer_MicrosoftVideoPageURLTooltip : undefined}
                      borderRadius={this.BORDER_RADIUS}
                      highlightColor={this.props.store!.highlightColor}
                      disabled={this.props.store!.activeComponent.video?.source !== "Link" && this.props.store!.activeComponent.video?.source !== "Microsoft"}
                      onChange={(url: string) => {
                        if (url.length > 0 && url !== "") {
                          this.props.store!.activeComponent.video.url = Helper.convertUrlToEmbedUrl(url);
                        } else {
                          this.props.store!.activeComponent.video.url = undefined;
                        }
                        this.props.onChange();
                      }}
                    />
                  }
                  {(this.props.store!.activeComponent.video?.source === "Embed" || this.props.store!.activeComponent.video?.source === "Microsoft") &&
                    <div>

                      <div style={{
                        fontSize: "14px",
                        marginTop: 20,
                        float: "left"
                      }}>
                        {this.localizationService.strings.Composer_EmbedCode}
                      </div>
                      {this.props.store!.activeComponent.video?.source === "Microsoft" &&
                        <IATooltip
                          content="Tooltip"
                          type="bubble"
                          rootStyles={{
                            marginTop: 2,
                            marginLeft: 5
                          }}
                          componentContent={
                            <div>
                              {this.localizationService.strings.Composer_MicrosoftVideoEmbedCodeTooltip}
                            </div>
                          }
                        >
                          <div style={{
                            fontSize: "14px",
                            marginTop: 20,
                            float: "left"
                          }}>
                            <IAIcon
                              title="Info"
                              size={14}
                              color="Black"
                            />
                          </div>
                        </IATooltip>
                      }
                      <textarea
                        value={this.props.store!.activeComponent && this.props.store!.activeComponent.video && this.props.store!.activeComponent.video?.embedCode}
                        placeholder={"<iframe... />"}
                        style={{
                          width: "calc(100% - 7px)",
                          border: "none",
                          minHeight: 100,
                          marginTop: 10,
                          fontSize: "13px",
                          padding: 7,
                          boxSizing: "border-box",
                          minWidth: "100%",
                          maxWidth: "100%",
                          borderRadius: this.BORDER_RADIUS
                        }}
                        onChange={(event) => {
                          if (event.target.value.length > 0 && event.target.value !== "") {
                            this.props.store!.activeComponent.video.embedCode = event.target.value;
                          } else {
                            this.props.store!.activeComponent.video.embedCode = undefined;
                          }
                          this.props.onChange();
                        }}
                      />
                    </div>
                  }
                  <IATextField
                    label={this.localizationService.strings.Composer_ALTText}
                    text={this.props.store!.activeComponent && this.props.store!.activeComponent.video ? this.props.store!.activeComponent.video?.altText : undefined}
                    style={{
                      marginTop: 20
                    }}
                    borderRadius={this.BORDER_RADIUS}
                    highlightColor={this.props.store!.highlightColor}
                    onChange={(altText: string) => {
                      if (altText.length > 0 && altText !== "") {
                        this.props.store!.activeComponent.video.altText = altText;
                      } else {
                        this.props.store!.activeComponent.video.altText = undefined;
                      }
                      this.props.onChange();
                    }}
                  />
                </div>
              }
            </div >
          );
          break;



        case "subjects":
          if (!this.props.store!.isMobile) {
            document.body.addEventListener('onmousedown', (event: any) => {
              var exceptions = '#IA_toolboxContent *';
              if (!event.target.matches(exceptions) && this.props.store!.activeComponent && this.props.store!.activeComponent.type === "subjects") {
                this.props.store!.activeComponent = undefined;
              }
            });
          }
          toolboxContent = (
            <div>
              <div className={styles.IA_componentInfo}>
                {this.props.store!.instance.settings?.categoriesRequiredToolTip ? this.props.store!.instance.settings?.categoriesRequiredToolTip : this.localizationService.strings.Composer_SubjectsInfo}
              </div>
              <div
                style={{
                  marginLeft: 20,
                  width: "calc(100% - 20px)"
                }}
              >
                <IATermCheckbox
                  environment={this.props.store!.environment}
                  token={this.props.store!.token}
                  tenant={this.props.store!.tenant}
                  instance={this.props.store!.instance}
                  component={this.props.store!.component}
                  highlightColor={this.props.store!.highlightColor}
                  selectedValues={this.props.store!.subjects.categories}
                  categoryDataItems={JSON.stringify(this.props.store!.categoryDataItems)}
                  onChange={(selectedValues) => {
                    this.props.store!.subjects.categories = selectedValues;
                    this.props.onChange();
                  }}
                  useColors
                />
              </div>
            </div>
          );
          break;
        case "attachments":
          if (!this.props.store!.isMobile) {
            document.body.addEventListener('onmousedown', (event: any) => {
              var exceptions = '#IA_toolboxContent *';
              if (!event.target.matches(exceptions) && this.props.store!.activeComponent && this.props.store!.activeComponent.type === "attachments") {
                this.props.store!.activeComponent = undefined;
              }
            });
          }
          const uploadedFiles = [];
          this.props.store!.activeComponent.files.forEach((file: IAFileUploaderFile, index) => {
            uploadedFiles.push(
              <div style={{
                float: "left",
                boxSizing: "border-box",
                width: "100%",
                marginTop: 20,
                borderTop: "1px dashed #333333",
                borderBottom: this.props.store!.activeComponent.files.length === index + 1 ? "1px dashed #333333" : "none",
                paddingBottom: this.props.store!.activeComponent.files.length === index + 1 ? 20 : 0,
                paddingTop: 6
              }}>
                <IATextField
                  label={this.localizationService.strings.Composer_FileName}
                  text={file.name ? file.name : this.localizationService.strings.Composer_Uploading}
                  style={{
                    marginTop: -2
                  }}
                  disabled
                  borderRadius={this.BORDER_RADIUS}
                  highlightColor={this.props.store!.highlightColor}
                  onChange={(url: string) => {
                    console.log(url); // Disabled
                  }}
                />
                <IATextField
                  label={this.localizationService.strings.Composer_ALTText}
                  text={file.altText}
                  style={{
                    marginTop: 5
                  }}
                  borderRadius={this.BORDER_RADIUS}
                  highlightColor={this.props.store!.highlightColor}
                  onChange={(altText) => {
                    this.props.store!.activeComponent.files[index].altText = altText;
                    this.props.onChange();
                  }}
                />
              </div>
            );
          })
          toolboxContent = (
            <div>
              <div className={styles.IA_componentInfo}>
                {this.localizationService.strings.Composer_AttachmentsInfo}
              </div>
              <IAFileUploader
                onChange={(files: IAFileUploaderFile[]) => {
                  this.props.store!.attachments.files = files;
                }}
                token={this.props.store!.token}
                tenant={this.props.store!.tenant}
                filesAttached={this.props.store!.attachments.files}
                linkColor={this.props.store!.highlightColor}
                uploadUrl={`${Environment.getEnvironmentForMessagesAPI(this.props.store!.environment)}`}
                idPicker={Helper.getRandomStringKey()}
                style={{
                  float: "left",
                  width: "100%"
                }}
              />
              {this.props.store!.activeComponent.files &&
                uploadedFiles
              }
            </div>
          );
          break;
        case "text":
          if (!this.props.store!.isMobile) {
            document.body.addEventListener('onmousedown', (event: any) => {
              var exceptions = '#IA_text *, #IA_toolboxContent *';
              if (!event.target.matches(exceptions) && this.props.store!.activeComponent && this.props.store!.activeComponent.type === "text") {
                this.props.store!.activeComponent = undefined;
                this.props.store!.showLinkEditor = false;
                this.props.store!.textSelection = undefined;
                this.props.onChange();
              }
            });
          }
          toolboxContent = (
            <div>
              <IATextField
                label={this.localizationService.strings.Composer_URL}
                text={this.props.store!.linkEditorUrl}
                style={{
                  marginTop: 20
                }}
                borderRadius={this.BORDER_RADIUS}
                highlightColor={this.props.store!.highlightColor}
                onChange={(newUrl: string) => {
                  this.props.store!.linkEditorUrl = newUrl;
                }}
              />
              <IATextField
                label={this.localizationService.strings.Composer_LinkText}
                text={this.props.store!.linkEditorTitle}
                style={{
                  marginTop: 20
                }}
                borderRadius={this.BORDER_RADIUS}
                highlightColor={this.props.store!.highlightColor}
                onChange={(newTitle: string) => {
                  this.props.store!.linkEditorTitle = newTitle;
                }}
              />
              <IACheckbox
                highlightColor={this.props.store!.highlightColor}
                isChecked={this.props.store!.linkEditorTarget === "_blank"}
                label={this.localizationService.strings.Composer_OpenInNewTab}
                onChange={() => {
                  this.props.store!.linkEditorTarget = this.props.store!.linkEditorTarget === "_blank" ? "_self" : "_blank";
                }}
                styles={{
                  float: "left",
                  marginTop: 20,
                }}
              />
              <IAButton
                label={this.localizationService.strings.Composer_AddLink}
                buttonColor={this.props.store!.highlightColor}
                onClick={() => {
                  if (this.props.store!.linkEditorRange) {
                    this.props.store!.linkEditorRange.deleteContents();
                    var a = document.createElement('a');
                    var linkText = document.createTextNode(this.props.store!.linkEditorTitle);
                    a.appendChild(linkText);
                    a.title = this.props.store!.linkEditorTitle;
                    a.target = this.props.store!.linkEditorTarget;
                    a.href = this.props.store!.linkEditorUrl;
                    a.setAttribute("data-interception", "off");
                    this.props.store!.linkEditorRange.insertNode(a);
                    this.props.store!.showLinkEditor = false;
                    this.props.store!.textSelection = undefined;
                  }
                }}
                style={{
                  marginTop: 20,
                  clear: "both",
                  float: "left"
                }}
                borderRadius={this.BORDER_RADIUS}
              />
            </div>
          );
          break;
        case "table":
          const cell: any = document.activeElement?.nodeName === "TH" ? document.activeElement : (document.getElementById(`${this.state.tableLastCellId}`) != null ? document.getElementById(`${this.state.tableLastCellId}`) : undefined);
          const row: any = cell?.parentNode?.nodeName === "TR" ? cell.parentNode : undefined;
          const table: any = document.getElementById(`IA_TableEditor_${this.props.store!.activeComponent.id}`);
          let borderColor
          if (cell && cell.id !== this.state.tableLastCellId) {
            this.setState({ tableLastCellId: cell.id, cellSelection: window.getSelection() });
            if (this.state.cellSelection !== null) {
              // the where the link should be inserted
              this.props.store!.linkEditorRange = this.state.cellSelection.getRangeAt(0);
            }
          }
          if (table) {
            const firstRow = table.childNodes[0];
            const firstCell: any = firstRow.childNodes[0];
            borderColor = firstCell.style.borderColor;
          }
          toolboxContent = (
            <div>
              {table &&
                <div style={{
                  marginLeft: 10,
                  float: "left",
                  width: "100%",
                  marginBottom: 20
                }}>
                  <h1
                    style={{
                      fontSize: 19,
                      width: "calc(100% - 20px)",
                      float: "left",
                      borderBottom: "1px solid #333333",
                      paddingBottom: 10
                    }}>
                    {this.localizationService.strings.Composer_TableSettings}
                  </h1>
                  <IAColorPicker
                    label={this.localizationService.strings.Composer_BorderColor}
                    color={borderColor ? Helper.convertRbToHex(borderColor) : "#eeeeee"}
                    defaultColor="#ffffff"
                    showClearButton={true}
                    onChange={(color: string) => {
                      table.childNodes.forEach((row: any) => {
                        row.childNodes.forEach((cell: any) => {
                          cell.style.borderColor = color;
                        })
                      })
                      this.updateTable();
                    }}
                    styles={{
                      clear: "both",
                      width: "50%"
                    }}
                  />
                </div>
              }
              {row &&
                <div style={{
                  marginLeft: 10,
                  float: "left",
                  width: "100%",
                  marginBottom: 20
                }}>
                  <h1
                    style={{
                      fontSize: 19,
                      width: "calc(100% - 20px)",
                      float: "left",
                      borderBottom: "1px solid #333333",
                      paddingBottom: 10
                    }}>
                    {this.localizationService.strings.Composer_RowSettings}
                  </h1>
                  <IAFontStyle
                    label={this.localizationService.strings.Composer_FontStyle}
                    selectedColor={this.props.store!.highlightColor}
                    fontWeight={row.style.fontWeight ? row.style.fontWeight : "normal"}
                    align={row.style.textAlign ? row.style.textAlign : "left"}
                    setFontWeight={(type: string) => {
                      row.style.fontWeight = type;
                      this.updateTable();
                    }}
                    setFontAlign={(align: string) => {
                      row.style.textAlign = align;
                      this.updateTable();
                    }}
                  />
                  <IAColorPicker
                    label={this.localizationService.strings.Composer_BackgroundColor}
                    color={row.style.backgroundColor ? Helper.convertRbToHex(row.style.backgroundColor) : "#ffffff"}
                    defaultColor="#ffffff"
                    showClearButton={true}
                    onChange={(color: string) => {
                      row.style.backgroundColor = color ? color : "none";
                      this.updateTable();
                    }}
                    styles={{
                      clear: "both",
                      width: "50%"
                    }}
                  />
                  <IAColorPicker
                    label={this.localizationService.strings.Composer_TextColor}
                    color={row.style.color ? Helper.convertRbToHex(row.style.color) : "#000000"}
                    defaultColor="#000000"
                    showClearButton={true}
                    onChange={(color: string) => {
                      row.style.color = color ? color : "none";
                      this.updateTable();
                    }}
                    styles={{
                      width: "50%"
                    }}
                  />
                </div>
              }
              {cell &&
                <div style={{
                  marginLeft: 10,
                  float: "left",
                  width: "100%",
                  marginBottom: 20
                }}>
                  <h1
                    style={{
                      fontSize: 19,
                      width: "calc(100% - 20px)",
                      float: "left",
                      borderBottom: "1px solid #333333",
                      paddingBottom: 10
                    }}>
                    {this.localizationService.strings.Composer_CellSettings}
                  </h1>
                  <IAFontStyle
                    label={this.localizationService.strings.Composer_FontStyle}
                    selectedColor={this.props.store!.highlightColor}
                    fontWeight={cell.style.fontWeight ? cell.style.fontWeight : (row.style.fontWeight ? row.style.fontWeight : undefined)}
                    align={cell.style.textAlign ? cell.style.textAlign : (row.style.textAlign ? row.style.textAlign : undefined)}
                    setFontWeight={(type: string) => {
                      cell.style.fontWeight = type;
                      this.updateTable();
                    }}
                    setFontAlign={(align: string) => {
                      cell.style.textAlign = align;
                      this.updateTable();
                    }}
                  />
                  <IAColorPicker
                    label={this.localizationService.strings.Composer_BackgroundColor}
                    color={cell.style.backgroundColor && cell.style.backgroundColor !== "transparent" ? Helper.convertRbToHex(cell.style.backgroundColor) : (row.style.backgroundColor ? Helper.convertRbToHex(row.style.backgroundColor) : undefined)}
                    defaultColor={undefined}
                    showClearButton={true}
                    onChange={(color: string) => {
                      cell.style.backgroundColor = color ? color : "transparent";
                      this.updateTable();
                    }}
                    styles={{
                      clear: "both",
                      width: "50%"
                    }}
                  />
                  <IAColorPicker
                    label={this.localizationService.strings.Composer_TextColor}
                    color={cell.style.color && cell.style.color !== "transparent" ? Helper.convertRbToHex(cell.style.color) : (row.style.color ? Helper.convertRbToHex(row.style.color) : undefined)}
                    defaultColor={undefined}
                    showClearButton={true}
                    onChange={(color: string) => {
                      cell.style.color = color ? color : "transparent";
                      this.updateTable();
                    }}
                    styles={{
                      width: "50%"
                    }}
                  />
                </div>
              }
              {cell &&
                <div style={{
                  marginRight: 15,
                  float: "left",
                  width: "100%",
                  marginBottom: 20,
                  marginLeft: 10
                }}>
                  <h1
                    style={{
                      fontSize: 19,
                      width: "calc(100% - 20px)",
                      float: "left",
                      borderBottom: "1px solid #333333",
                      paddingBottom: 10
                    }}>
                    {this.localizationService.strings.Composer_InsertLinkIntoTable}
                  </h1>
                  <div>
                    <IATextField
                      label={this.localizationService.strings.Composer_LinkText}
                      text={this.props.store!.linkEditorTitle}
                      style={{
                        marginTop: 10,
                        width: "calc(100% - 20px)"
                      }}
                      borderRadius={this.BORDER_RADIUS}
                      highlightColor={this.props.store!.highlightColor}
                      onChange={(newTitle: string) => {
                        this.props.store!.linkEditorTitle = newTitle;
                      }}
                    />
                    <IATextField
                      label={this.localizationService.strings.Composer_URL}
                      text={this.props.store!.linkEditorUrl}
                      style={{
                        marginTop: 10,
                        width: "calc(100% - 20px)"
                      }}
                      borderRadius={this.BORDER_RADIUS}
                      highlightColor={this.props.store!.highlightColor}
                      onChange={(newUrl: string) => {
                        this.props.store!.linkEditorUrl = newUrl;
                      }}
                    />
                    {/* <IACheckbox
                      highlightColor={this.props.store!.highlightColor}
                      isChecked={this.props.store!.linkEditorTarget === "_blank"}
                      label={this.localizationService.strings.Composer_OpenInNewTab}
                      onChange={() => {
                        this.props.store!.linkEditorTarget = this.props.store!.linkEditorTarget === "_blank" ? "_self" : "_blank";
                      }}
                      styles={{
                        float: "left",
                        marginTop: 20,
                      }}
                    /> */}
                    <IAButton
                      label={this.localizationService.strings.Composer_AddLink}
                      buttonColor={this.props.store!.highlightColor}
                      onClick={() => {
                        if (this.props.store!.linkEditorUrl !== "" && this.props.store!.linkEditorUrl !== undefined) {
                          var a = document.createElement('a');
                          var linkTitle = (this.props.store!.linkEditorTitle === "" || this.props.store!.linkEditorTitle === undefined) ? this.props.store!.linkEditorUrl : this.props.store!.linkEditorTitle;
                          var linkText = document.createTextNode(linkTitle);
                          a.appendChild(linkText);
                          a.title = linkTitle;
                          a.target = this.props.store!.linkEditorTarget === undefined ? "_self" : this.props.store!.linkEditorTarget;
                          a.href = this.props.store!.linkEditorUrl;
                          a.setAttribute("data-interception", "off");
                          let insertIntoCell = document.getElementById(this.state.tableLastCellId);
                          insertIntoCell.appendChild(a);
                          this.props.store!.linkEditorUrl = "";
                          this.props.store!.linkEditorTitle = "";
                          this.updateTable();
                        }
                      }}
                      style={{
                        marginTop: 20,
                        clear: "both",
                        float: "left"
                      }}
                      borderRadius={this.BORDER_RADIUS}
                    />
                  </div>
                </div>
              }
            </div>
          );
          break;
        default:
          break;
      }
    }
    return (
      <div
        className={styles.IA_toolbox}
        style={{ height: !this.props.store!.isMobile ? `calc(100% - ${44 + this.props.store!.marginTop}px)` : "" }}
      >
        {!this.props.store!.isMobile &&
          <div
            style={{
              background: "linear-gradient(90deg,transparent,#aaa)",
              height: "100%",
              opacity: .2,
              pointerEvents: "none",
              position: "absolute",
              left: 290,
              width: 10,
              marginTop: "-25px"
            }}
          />
        }
        <div className={styles.IA_toolboxContainer}>
          {(this.props.store!.selectedComposerTab === "content" || this.props.store!.selectedComposerTab === "replay" || this.props.store!.selectedComposerTab === "newsLink") && (this.props.store!.activeComponent && (this.props.store!.activeComponent.type === "image" || this.props.store!.activeComponent.type === "button" || this.props.store!.activeComponent.type === "divider" || this.props.store!.activeComponent.type === "video" || this.props.store!.activeComponent.type === "audio" || this.props.store!.activeComponent.type === "gallery" || this.props.store!.activeComponent.type === "cover" || this.props.store!.activeComponent.type === "subjects" || this.props.store!.activeComponent.type === "attachments" || this.props.store!.activeComponent.type === "table" || (this.props.store!.activeComponent.type === "text" && this.props.store!.showLinkEditor))) ?
            <div
              className={styles.IA_toolboxContent}
              id={"IA_toolboxContent"}
            >
              {toolboxContent}
            </div>
            :
            <Guide />
          }
        </div>
      </div>
    );
  }


}